import React, { useState, useEffect, useMemo } from "react";
import T from '../../components/common/T';
import api from "../../api/axiosConfig";
import Price from "../../components/common/Price";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import useModuleFilters from "../../hooks/useModuleFilters";
import { 
  ArrowPathIcon, 
  FunnelIcon, 
  DocumentTextIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  XMarkIcon,
  EyeIcon
} from "@heroicons/react/24/outline";

export default function GeneralLedger() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("general-ledger", {
    accounts: [],
  });
  
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const [stats, setStats] = useState({
    totalDebits: 0, totalCredits: 0, balance: 0, transactionCount: 0
  });

  const fetchAccounts = async () => {
    try {
      const res = await api.get("/accounting/chart-of-accounts");
      setAccounts(res.data);
    } catch (err) {}
  };

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/ledger");
      
      const grouped = res.data.reduce((acc, row) => {
        if (!acc[row.id]) {
          acc[row.id] = { id: row.id, date: row.date, reference: row.reference, description: row.description, status: row.status, lines: [] };
        }
        acc[row.id].lines.push({ account_code: row.account_code, account_name: row.account_name, debit: parseFloat(row.debit) || 0, credit: parseFloat(row.credit) || 0 });
        return acc;
      }, {});

      const entriesList = Object.values(grouped);
      setEntries(entriesList);

      const totalDebits = res.data.reduce((sum, r) => sum + (parseFloat(r.debit) || 0), 0);
      const totalCredits = res.data.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);
      
      setStats({ totalDebits, totalCredits, balance: totalDebits - totalCredits, transactionCount: entriesList.length });
    } catch (err) {
      console.error("Error loading ledger:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    fetchAccounts();
  }, []);

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = !searchTerm || entry.reference?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAccount = !filterAccount || entry.lines.some(line => line.account_code === filterAccount);
    const matchesDate = (!startDate || entry.date >= startDate) && (!endDate || entry.date <= endDate);
    return matchesSearch && matchesAccount && matchesDate;
  });

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setShowDetails(true);
  };

  const clearFilters = () => {
    setSearchTerm(""); setFilterAccount(""); setStartDate(""); setEndDate("");
  };

  const columns = [
    {
      header: "Date",
      accessor: "date",
      sortable: true,
      render: (entry) => (
        <div className="flex items-center gap-1.5 text-gray-700 font-medium">
            <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
            {new Date(entry.date).toLocaleDateString('en-IN')}
        </div>
      )
    },
    {
      header: "Reference",
      accessor: "reference",
      sortable: true,
      render: (entry) => <span className="font-mono text-[11px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 font-bold">{entry.reference || 'N/A'}</span>
    },
    { header: "Description", accessor: "description", render: (entry) => <span className="truncate max-w-[150px] inline-block text-gray-600">{entry.description}</span> },
    {
      header: "Accounts",
      render: (entry) => (
        <div className="flex flex-wrap gap-1">
          {entry.lines.slice(0, 2).map((line, idx) => (
            <span key={idx} className="text-[10px] font-mono bg-gray-100 border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
              {line.account_code}
            </span>
          ))}
          {entry.lines.length > 2 && <span className="text-[10px] text-gray-500 font-bold">+{entry.lines.length - 2}</span>}
        </div>
      )
    },
    {
      header: "Debit",
      align: "right",
      render: (entry) => {
        const total = entry.lines.reduce((sum, line) => sum + line.debit, 0);
        return <span className="font-mono text-green-700 font-bold">{total > 0 ? `₹${total.toLocaleString()}` : '-'}</span>;
      }
    },
    {
      header: "Credit",
      align: "right",
      render: (entry) => {
        const total = entry.lines.reduce((sum, line) => sum + line.credit, 0);
        return <span className="font-mono text-red-700 font-bold">{total > 0 ? `₹${total.toLocaleString()}` : '-'}</span>;
      }
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (entry) => <span className="inline-flex px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 rounded text-[10px] font-bold uppercase tracking-wide">{entry.status || 'Posted'}</span>
    },
    {
      header: "View",
      align: "right",
      render: (entry) => (
        <TableActionButtons
          onView={() => handleEntryClick(entry)}
          viewTitle="View Details"
        />
      )
    }
  ];

  const statsList = useMemo(() => [
    {
      label: "Transactions",
      value: stats.transactionCount,
      icon: DocumentTextIcon,
      variant: "default",
    },
    {
      label: "Total Debits",
      value: <Price amount={stats.totalDebits} />,
      icon: ArrowTrendingUpIcon,
      variant: "green",
    },
    {
      label: "Total Credits",
      value: <Price amount={stats.totalCredits} />,
      icon: ArrowTrendingDownIcon,
      variant: "red",
    },
    {
      label: "Balance Check",
      value: <Price amount={Math.abs(stats.balance).toFixed(2)} />,
      sub: Math.abs(stats.balance) < 1 ? "Balanced ✓" : "Out of Balance",
      icon: ChartBarIcon,
      variant: Math.abs(stats.balance) < 1 ? "blue" : "red",
    },
  ], [stats]);

  return (
    <PageLayout>
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="General Ledger"
        subtitle="Complete financial transaction history with double-entry bookkeeping"
        onRefresh={fetchLedger}
        loading={loading}
      />

      {/* Stats Cards */}
      <StatsGrid stats={statsList} cols={4} />

      {/* Unified Toolbar */}
      <Toolbar 
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search reference or description..."
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Filter Drawer */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={clearFilters}
      >
        <div>
          <label className="block text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase mb-1 flex items-center justify-between">
            <span><T>Account</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]" 
            value={filterAccount} 
            onChange={e => setFilterAccount(e.target.value)}
          >
            <option value=""><T>All Accounts</T></option>
            {((filterOptions.accounts?.length > 0 ? filterOptions.accounts : accounts) || []).map(acc => (
              <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase mb-1"><T>Start Date</T></label>
          <input type="date" className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase mb-1"><T>End Date</T></label>
          <input type="date" className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
      </FilterPanel>

      {/* Table Card */}
      <TableCard>
        <DataTable data={filteredEntries} columns={columns} emptyMessage="No transactions found." />
      </TableCard>

      {/* Details Modal remains unchanged, just styling the wrapper to match the theme */}
      {showDetails && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl animate-fade-in overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <div>
                <h3 className="text-base font-bold"><T>Journal Entry Details</T></h3>
                <p className="text-purple-100 text-xs font-mono mt-0.5">{selectedEntry.reference}</p>
              </div>
              <button onClick={() => setShowDetails(false)} className="p-1.5 hover:bg-purple-500 rounded transition"><XMarkIcon className="w-5 h-5"/></button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                <div><p className="text-[10px] text-gray-400 uppercase font-bold"><T>Date</T></p><p className="text-sm text-gray-900 font-bold mt-0.5">{new Date(selectedEntry.date).toLocaleDateString('en-IN')}</p></div>
                <div><p className="text-[10px] text-gray-400 uppercase font-bold"><T>Reference</T></p><p className="text-sm text-purple-700 font-mono font-bold mt-0.5">{selectedEntry.reference}</p></div>
                <div className="col-span-2"><p className="text-[10px] text-gray-400 uppercase font-bold"><T>Description</T></p><p className="text-sm text-gray-800 font-medium mt-0.5">{selectedEntry.description}</p></div>
              </div>

              <div>
                <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-3"><T>Journal Lines</T></h4>
                <div className="space-y-2">
                  {selectedEntry.lines.map((line, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-200">
                      <div className="flex-1"><p className="text-sm font-bold text-gray-800">{line.account_code} - {line.account_name}</p></div>
                      <div className="flex gap-6">
                        <div className="text-right w-24"><p className="text-[10px] text-gray-400 uppercase font-bold mb-1"><T>Debit</T></p><p className="font-mono font-bold text-green-700 text-sm">{line.debit > 0 ? `₹${line.debit.toLocaleString()}` : '-'}</p></div>
                        <div className="text-right w-24"><p className="text-[10px] text-gray-400 uppercase font-bold mb-1"><T>Credit</T></p><p className="font-mono font-bold text-red-700 text-sm">{line.credit > 0 ? `₹${line.credit.toLocaleString()}` : '-'}</p></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center font-bold">
                    <span className="text-gray-600 uppercase text-xs tracking-wide"><T>Totals</T></span>
                    <div className="flex gap-6">
                      <div className="text-right w-24 text-green-700 text-sm"><Price amount={selectedEntry.lines.reduce((sum, l) => sum + l.debit, 0)} /></div>
                      <div className="text-right w-24 text-red-700 text-sm"><Price amount={selectedEntry.lines.reduce((sum, l) => sum + l.credit, 0)} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}